<template>
  <v-app>
    <v-main class="auth-page">
      <v-container fluid class="fill-height">
        <v-row justify="center" align="center" class="fill-height">
          <v-col cols="12" sm="8" md="6" lg="4" xl="3">
            <v-card class="auth-card" elevation="24" rounded="xl">
              <v-card-text class="pa-8">
                <!-- Header Section -->
                <div class="text-center mb-8">
                  <div>
                    <img
                      src="/images/logo-colegio.jpeg"
                      alt="Logo Colegio Guanentá"
                      class="school-logo"
                    />
                  </div>
                  <h1 class="auth-title mb-2">Bienvenido de nuevo</h1>
                  <p class="auth-subtitle text-medium-emphasis">
                    Inicia sesión para continuar tu jornada
                  </p>
                </div>

                <!-- Form Section -->
                <v-form @submit.prevent="handleSubmit" ref="form">
                  <!-- Email Field -->
                  <v-text-field
                    v-model="formData.email"
                    type="email"
                    label="Correo electrónico"
                    prepend-inner-icon="mdi-email"
                    variant="outlined"
                    rounded="lg"
                    class="mb-4"
                    :error-messages="emailError"
                    @blur="validateEmail"
                    @focus="clearFieldError('email')"
                    density="comfortable"
                  />

                  <!-- Password Field -->
                  <v-text-field
                    v-model="formData.password"
                    :type="showPassword ? 'text' : 'password'"
                    label="Contraseña"
                    prepend-inner-icon="mdi-lock"
                    :append-inner-icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'"
                    variant="outlined"
                    rounded="lg"
                    class="mb-4"
                    :error-messages="passwordError"
                    @blur="validatePassword"
                    @focus="clearFieldError('password')"
                    @click:append-inner="showPassword = !showPassword"
                    density="comfortable"
                  />

                  <!-- Error Alert -->
                  <v-alert
                    v-if="error"
                    type="error"
                    variant="tonal"
                    closable
                    rounded="lg"
                    class="mb-6"
                    @click:close="clearError"
                  >
                    {{ error }}
                  </v-alert>

                  <!-- Submit Button -->
                  <v-btn
                    type="submit"
                    block
                    size="large"
                    rounded="lg"
                    class="auth-button mb-6"
                    :loading="loading"
                    :disabled="!isFormValid || loading"
                    elevation="2"
                  >
                    <template #prepend>
                      <v-icon>mdi-login</v-icon>
                    </template>
                    {{ loading ? 'Iniciando Sesión...' : 'Iniciar Sesión' }}
                  </v-btn>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useTokenRefresh } from '@/composables/useTokenRefresh'

const router = useRouter()
const store = useStore()
const { startTokenRefresh } = useTokenRefresh()

const formData = ref({
  email: '',
  password: '',
})

const showPassword = ref(false)

// Individual field errors
const emailError = ref('')
const passwordError = ref('')

// Validation functions
const validateEmail = () => {
  if (!formData.value.email) {
    emailError.value = 'El correo electrónico es requerido'
    return false
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.value.email)) {
    emailError.value = 'Por favor, ingresa un correo electrónico válido'
    return false
  }
  emailError.value = ''
  return true
}

const validatePassword = () => {
  if (!formData.value.password) {
    passwordError.value = 'La contraseña es requerida'
    return false
  }
  passwordError.value = ''
  return true
}

const clearFieldError = (field) => {
  switch (field) {
    case 'email':
      emailError.value = ''
      break
    case 'password':
      passwordError.value = ''
      break
  }
}

const isFormValid = computed(() => {
  return (
    formData.value.email && formData.value.password && !emailError.value && !passwordError.value
  )
})

const loading = computed(() => store.getters['auth/loading'])
const error = computed(() => store.getters['auth/error'])

const clearError = () => {
  store.dispatch('auth/clearError')
}

const handleSubmit = async () => {
  // Validate all fields
  const emailValid = validateEmail()
  const passwordValid = validatePassword()

  if (!emailValid || !passwordValid) {
    return
  }

  const result = await store.dispatch('auth/login', formData.value)

  if (result.success) {
    startTokenRefresh()
    const dashboardRoute = store.getters['auth/getDashboardRoute']
    router.push(dashboardRoute)
  }
}

// Redirect if already authenticated
onMounted(() => {
  if (store.getters['auth/isLoggedIn']) {
    const dashboardRoute = store.getters['auth/getDashboardRoute']
    router.push(dashboardRoute)
  }
})
</script>

<style scoped>
.auth-page {
  position: relative;
  background-color: #ffffff;
  min-height: 100vh;
}

.auth-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

/* Opción 2: Logo decorativo */
.decorative-logo {
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 0;
}

.school-logo {
  width: 120px;
  height: auto;
  margin-bottom: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  opacity: 1;
  transition: transform 0.3s ease;
  /* Opcional: descomenta la siguiente línea para animación flotante */
  /* animation: gentle-float 4s ease-in-out infinite; */
}

.school-logo:hover {
  transform: scale(1.05);
}

/* Opcional: animación suave sin cambio de opacidad */
@keyframes gentle-float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
}

/* Alternativa: Logo en esquina */
.corner-logo {
  position: absolute;
  bottom: 20px;
  right: 20px;
  width: 80px;
  height: auto;
  opacity: 0.1;
  z-index: 0;
}

.auth-card {
  position: relative;
  z-index: 1;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

.logo-avatar {
  background: linear-gradient(135deg, #0a192f 0%, #112240 100%) !important;
  box-shadow: 0 10px 30px rgba(10, 25, 47, 0.3);
}

.auth-title {
  font-size: 2rem;
  font-weight: 700;
  color: #0a192f;
  letter-spacing: -0.025em;
}

.auth-subtitle {
  font-size: 1rem;
  margin: 0;
}

.auth-button {
  background: linear-gradient(135deg, #0a192f 0%, #112240 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  transition: all 0.3s ease !important;
}

.auth-button:hover:not(:disabled) {
  transform: translateY(-2px) !important;
  box-shadow: 0 10px 30px rgba(10, 25, 47, 0.3) !important;
}

.auth-button:active:not(:disabled) {
  transform: translateY(0) !important;
}

/* Responsive Design */
@media (max-width: 960px) {
  .auth-card {
    margin: 16px;
  }
}

@media (max-width: 600px) {
  .auth-card :deep(.v-card-text) {
    padding: 24px 20px !important;
  }

  .auth-title {
    font-size: 1.75rem;
  }

  .logo-avatar {
    width: 64px !important;
    height: 64px !important;
  }

  .logo-avatar :deep(.v-icon) {
    font-size: 28px !important;
  }
}

@media (max-width: 400px) {
  .auth-card :deep(.v-card-text) {
    padding: 20px 16px !important;
  }

  .auth-title {
    font-size: 1.5rem;
  }
}

.auth-card :deep(.v-field--focused .v-label) {
  color: #0a192f !important;
}

.auth-card :deep(.v-field--focused .v-field__prepend-inner .v-icon) {
  color: #0a192f !important;
}

.auth-card :deep(.v-text-field .v-field__prepend-inner .v-icon) {
  transition: color 0.3s ease;
}
</style>
